import { ArrowDownRight, ArrowUpRight, Minus } from 'lucide-react';
import type { ReactNode } from 'react';

import { cn } from '@/lib/utils';

import { type Trend, formatCount } from '../lib/format';

/** Series colours. Blue is Borrah's own; the rest differ in lightness too. */
export const SERIES = {
  primary: 'bg-primary',
  secondary: 'bg-orange-500',
  tertiary: 'bg-violet-500',
  upcoming: 'bg-primary/35',
  muted: 'bg-zinc-300 dark:bg-zinc-600',
} as const;

export function ReportSection({
  id,
  icon,
  title,
  description,
  action,
  children,
}: {
  id: string;
  icon: ReactNode;
  title: string;
  description: string;
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section
      id={id}
      aria-labelledby={`${id}-title`}
      className="flex scroll-mt-4 flex-col gap-4"
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary [&_svg]:size-[18px]">
            {icon}
          </span>
          <div>
            <h2
              id={`${id}-title`}
              className="font-semibold text-lg tracking-tight"
            >
              {title}
            </h2>
            <p className="text-muted-foreground text-sm">{description}</p>
          </div>
        </div>
        {action}
      </div>
      {children}
    </section>
  );
}

export function Panel({
  title,
  description,
  action,
  className,
  children,
}: {
  title?: string;
  description?: string;
  action?: ReactNode;
  className?: string;
  children: ReactNode;
}) {
  return (
    <div
      className={cn(
        'flex min-w-0 flex-col gap-4 rounded-xl border bg-card p-5 shadow-xs',
        className
      )}
    >
      {title ? (
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h3 className="font-semibold text-[15px]">{title}</h3>
            {description ? (
              <p className="mt-0.5 text-muted-foreground text-sm">
                {description}
              </p>
            ) : null}
          </div>
          {action}
        </div>
      ) : null}
      {children}
    </div>
  );
}

const TREND_ICON = { up: ArrowUpRight, down: ArrowDownRight, flat: Minus };

export function ChangeBadge({
  trend,
  label,
  goodWhen = 'up',
}: {
  trend: Trend;
  label: string;
  /** Which direction is good news: fewer no-shows is a fall. */
  goodWhen?: 'up' | 'down';
}) {
  const Icon = TREND_ICON[trend];
  const tone =
    trend === 'flat'
      ? 'bg-muted text-muted-foreground'
      : trend === goodWhen
        ? 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400'
        : 'bg-red-500/10 text-red-700 dark:text-red-400';
  return (
    <span
      className={cn(
        'inline-flex items-center gap-0.5 whitespace-nowrap rounded-full py-0.5 pr-2 pl-1.5 font-semibold text-xs tabular-nums',
        tone
      )}
    >
      <Icon className="size-3" strokeWidth={2.5} aria-hidden />
      {label}
    </span>
  );
}

export function StatCard({
  label,
  value,
  change,
  goodWhen,
  footnote,
}: {
  label: string;
  value: string;
  change?: { trend: Trend; label: string } | null;
  goodWhen?: 'up' | 'down';
  footnote: string;
}) {
  return (
    <div className="flex flex-col gap-3 rounded-xl border bg-card p-5 shadow-xs">
      <div className="flex items-center justify-between gap-2">
        <span className="font-medium text-muted-foreground text-sm">
          {label}
        </span>
        {change ? <ChangeBadge {...change} goodWhen={goodWhen} /> : null}
      </div>
      <span className="font-semibold text-3xl tabular-nums tracking-tight">
        {value}
      </span>
      <span className="text-muted-foreground text-xs">{footnote}</span>
    </div>
  );
}

/** A labelled number inside a panel. */
export function Figure({
  label,
  value,
  hint,
  swatch,
}: {
  label: string;
  value: string;
  hint?: ReactNode;
  swatch?: string;
}) {
  return (
    <div className="min-w-0">
      <p className="flex items-center gap-1.5 font-medium text-muted-foreground text-sm">
        {swatch ? (
          <span className={cn('size-2.5 shrink-0 rounded-[3px]', swatch)} />
        ) : null}
        {label}
      </p>
      <p className="mt-1 font-semibold text-2xl tabular-nums tracking-tight">
        {value}
      </p>
      {hint ? (
        <p className="mt-1 text-muted-foreground text-xs">{hint}</p>
      ) : null}
    </div>
  );
}

/** One bar split into its parts, with the parts named underneath. */
export function SplitBar({
  segments,
  label,
  legend = true,
}: {
  segments: Array<{ label: string; value: number; color: string }>;
  label: string;
  /** Off when the figures above already carry the same swatches and counts. */
  legend?: boolean;
}) {
  const total = segments.reduce((sum, s) => sum + s.value, 0);
  const visible = segments.filter((s) => s.value > 0);
  return (
    <div className="flex flex-col gap-3">
      <div
        role="img"
        aria-label={`${label}: ${segments.map((s) => `${s.label} ${s.value}`).join(', ')}`}
        className="flex h-3 gap-0.5 overflow-hidden rounded-full bg-muted"
      >
        {total > 0
          ? visible.map((s) => (
              <span
                key={s.label}
                className={cn(
                  'h-full first:rounded-l-full last:rounded-r-full',
                  s.color
                )}
                style={{ flexGrow: s.value, flexBasis: 0 }}
                title={`${s.label}: ${s.value}`}
              />
            ))
          : null}
      </div>
      {legend ? (
        <div className="flex flex-wrap gap-x-5 gap-y-1.5 text-sm">
          {segments.map((s) => (
            <span key={s.label} className="inline-flex items-center gap-1.5">
              <span className={cn('size-2.5 rounded-[3px]', s.color)} />
              <span className="text-muted-foreground">{s.label}</span>
              <span className="font-semibold tabular-nums">
                {formatCount(s.value)}
              </span>
            </span>
          ))}
        </div>
      ) : null}
    </div>
  );
}

/** Ranked rows with a bar scaled to the largest, for "where from" and "who". */
export function BarList({
  items,
  color = SERIES.primary,
  empty,
}: {
  items: Array<{ label: ReactNode; value: number; key: string; hint?: string }>;
  color?: string;
  empty: string;
}) {
  if (items.length === 0) {
    return <p className="text-muted-foreground text-sm">{empty}</p>;
  }
  const max = Math.max(...items.map((i) => i.value), 1);
  const total = items.reduce((sum, i) => sum + i.value, 0);
  return (
    <ul className="flex flex-col gap-3.5">
      {items.map((item) => (
        <li
          key={item.key}
          className="grid grid-cols-[minmax(0,10rem)_minmax(0,1fr)_auto] items-center gap-3 text-sm"
        >
          <span className="truncate font-medium">{item.label}</span>
          <span className="h-2 overflow-hidden rounded-full bg-muted">
            <span
              className={cn('block h-full rounded-full', color)}
              style={{ width: `${Math.max(2, (item.value / max) * 100)}%` }}
            />
          </span>
          <span className="text-right tabular-nums">
            <span className="font-semibold">{formatCount(item.value)}</span>
            <span className="ml-1.5 text-muted-foreground text-xs">
              {item.hint ?? `${Math.round((item.value / total) * 100)}%`}
            </span>
          </span>
        </li>
      ))}
    </ul>
  );
}

export function Legend({
  items,
}: {
  items: Array<{ label: string; color: string }>;
}) {
  return (
    <div className="flex flex-wrap gap-x-4 gap-y-1 text-muted-foreground text-xs">
      {items.map((item) => (
        <span key={item.label} className="inline-flex items-center gap-1.5">
          <span className={cn('size-2.5 rounded-[3px]', item.color)} />
          {item.label}
        </span>
      ))}
    </div>
  );
}
